/**
 * RIVYO → EN translator (JSON asset map with {title, body})
 * - ONLY on product pages
 * - ONLY on EN locale
 * - Loads Shopify asset: rivyo-pt-to-en.json
 * - Translates:
 *    - Title: h5... => mapValue.title
 *    - Snippet: first span inside p.description => shortened mapValue.body (keeps layout stable)
 *    - Read more full text: span.wc_review_read_more[data-desc] => mapValue.body (full)
 */

const RIVYO_UI_TRANSLATIONS = {
  "As opiniões dos nossos clientes": "Customer reviews",
  "Baseado em": "Based on",
  "avaliações": "reviews",
  "Avaliações": "Reviews",
  "Escrever avaliação": "Write a review",
  "Sortear por": "Sort by",
  "Eu recomendo este produto": "I recommend this product",
  "Ler mais": "Read more"
};

const UI_TEXT_SELECTORS = [
  "#wc_review_section h2",
  ".wc_review_summary_count_text",
  ".wc_rivyo_tab_toggle",
  ".wcRivyoFeedback span",
  ".wc_rivyo_review_filter label",
  ".wc_review_item_recommend_product span",
  ".wc_review_read_more"
];

const RIVYO_JSON_ASSET_FILENAME = "rivyo-pt-to-en.json";
const DEBUG = true;

const TITLE_SEL = "h5.wc_review_item_body_title.name";
const SNIPPET_SEL = "p.description > span:first-child";
const READMORE_SEL = "span.wc_review_read_more[data-desc]";


const MAX_PER_PASS = 200;

(function main() {
  // ---- Locale gate: EN only ----
  const isEnLocale =
    document.documentElement.lang?.toLowerCase().startsWith("en") ||
    /^\/en(\/|$)/i.test(location.pathname);

  if (!isEnLocale) return;

  // ---- Product gate ----
  const isProductPage =
    /\/products\//.test(location.pathname) ||
    document.body.classList.contains("template-product") ||
    document.documentElement.classList.contains("template-product");

  if (!isProductPage) return;

  log("[Rivyo translate] starting on", location.pathname);

  waitFor(() => document.querySelector("#wc_review_section, .wc_rivyo_widget_root, .wc_review_main_content"), 12000)
    .then(() => boot())
    .catch(() => log("[Rivyo translate] Rivyo root not found; exiting"));
})();

async function boot() {
  try {
    const assetUrl = getShopifyAssetUrl(RIVYO_JSON_ASSET_FILENAME);
    if (!assetUrl) {
      log("[Rivyo translate] could not compute asset_url. Ensure script included via asset_url | script_tag.");
      return;
    }

    log("[Rivyo translate] loading map", assetUrl);

    const rawMap = await fetchJsonNoCache(assetUrl);

    // rawMap values MUST look like: { title: "...", body: "..." }
    const idx = buildIndexes(rawMap);

    log(`[Rivyo translate] map loaded. keys: ${Object.keys(rawMap).length}`);

    // First pass
    translateAll(idx);

    // Observe changes (pagination, lazy render, filters)
    const root = document.querySelector("#wc_review_section") || document.body;
    observeRivyo(root, () => translateAll(idx));

    // Re-run on user interactions (extra safety)
    wireUserActions(() => translateAll(idx));
  } catch (e) {
    console.error("[Rivyo translate] boot error:", e);
  }
}

function wireModalTranslation(idx) {
  // If Rivyo injects a modal anywhere, catch it and translate its text nodes once.
  const obs = new MutationObserver(() => {
    // common-ish patterns: dialog/modal containers
    const modal =
      document.querySelector(".wc_review_quickview_modal, .wc_quickview_modal, .wc_modal, .wc-popup, [role='dialog']");

    if (!modal) return;
    if (modal.dataset.rivyoModalTranslated === "1") return;

    // translate any title/body inside the modal
    let translated = 0;

    // Try a few likely selectors (safe even if none exist)
    const candidates = modal.querySelectorAll("h1,h2,h3,h4,h5,p,span,div");

    for (const el of candidates) {
      // only translate leaf-ish nodes (avoid scrambling layout)
      if (!el || el.children.length) continue;

      translated += translateElementText(el, idx);
    }

    modal.dataset.rivyoModalTranslated = "1";
    if (DEBUG) console.log("[Rivyo translate] modal translated:", translated);
  });

  obs.observe(document.body, { childList: true, subtree: true });
}

function translateDataDesc(el, idx) {
  if (!el || el.nodeType !== 1) return 0;

  const original = (el.getAttribute("data-desc") || "").trim();
  if (!original) return 0;

  // Same “don’t trust the flag across pagination” logic
  const applied = el.dataset.rivyoAppliedDesc || "";
  if (el.dataset.rivyoTranslatedDesc === "1" && applied === original) {
    // If data-desc currently equals what we already applied, skip
    return 0;
  }

  const translated = lookup(original, idx);
  if (!translated) return 0;

  el.setAttribute("data-desc", translated);
  el.dataset.rivyoTranslatedDesc = "1";
  el.dataset.rivyoAppliedDesc = translated; // what we applied last time
  el.dataset.rivyoOriginalDesc = original;

  return 1;
}

function translateAll(idx) {
  const root = document.querySelector("#wc_review_section") || document.body;
  if (!root) return;

  let translatedCount = 0;

  // ---- Snippets (keep same length to avoid layout scramble) ----
  const snippetEls = root.querySelectorAll(SNIPPET_SEL);
  for (const el of snippetEls) {
    if (translatedCount >= MAX_PER_PASS) break;
    translatedCount += translateSnippet(el, idx);
  }

  // ---- Read-more full body (data-desc) ----
  const readMoreEls = root.querySelectorAll(READMORE_SEL);
  for (const el of readMoreEls) {
    if (translatedCount >= MAX_PER_PASS) break;
    translatedCount += translateDataDesc(el, idx);
  }

  // ---- Titles (AFTER body pass caches title on <li>) ----
  const titleEls = root.querySelectorAll(TITLE_SEL);
  for (const el of titleEls) {
    if (translatedCount >= MAX_PER_PASS) break;
    translatedCount += translateTitle(el, idx);
  }

  translateRivyoUiText(root);
  //wireModalTranslation(idx);

  log(`[Rivyo translate] translated this pass: ${translatedCount}; map size: ${idx.exact.size}`);
}

/* ------------------- Translators ------------------- */

function translateTitle(el, idx) {
  if (!el || el.nodeType !== 1) return 0;

  const li = el.closest("li.wc_review_widget_item");
  const cachedTitle = (li?.dataset?.rivyoEnTitle || "").trim();
  if (!cachedTitle) return 0;

  const current = (el.textContent || "").trim();

  // ✅ if it's already the desired English title, do nothing
  if (current === cachedTitle) {
    el.dataset.rivyoTitleTranslated = "1";
    el.dataset.rivyoTitleApplied = cachedTitle;
    return 0;
  }

  // ✅ if we already applied this and it's still there, skip
  if (el.dataset.rivyoTitleTranslated === "1" && el.dataset.rivyoTitleApplied === current) {
    return 0;
  }

  el.dataset.rivyoOriginalTitle = current;
  el.textContent = cachedTitle;

  el.dataset.rivyoTitleTranslated = "1";
  el.dataset.rivyoTitleApplied = cachedTitle;

  return 1;
}

function translateSnippet(el, idx) {
  if (!el || el.nodeType !== 1) return 0;
  if (el.dataset.rivyoSnippetTranslated === "1") return 0;

  const original = (el.textContent || "").trim();
  if (!original) {
    el.dataset.rivyoSnippetTranslated = "1";
    return 0;
  }

  const hit = lookup(original, idx);
  const body = hit && typeof hit.body === "string" ? hit.body.trim() : "";
  if (!body) return 0;

    // Cache English title on the parent <li> so title pass can apply it later
  const li = el.closest("li.wc_review_widget_item");
  if (li && hit && typeof hit.title === "string" && hit.title.trim()) {
    li.dataset.rivyoEnTitle = hit.title.trim();
  }

  // Keep same “visual size” as original snippet to prevent masonry layout from exploding
  const keepEllipsis = endsWithEllipsis(original);
  const targetLen = stripEllipsis(original).length;

  const shortened = shortenToLength(body, targetLen, keepEllipsis);

  el.textContent = shortened;
  el.dataset.rivyoSnippetTranslated = "1";
  el.dataset.rivyoOriginalSnippet = original;
  return 1;
}

function translateReadMoreDataDesc(el, idx) {
  if (!el || el.nodeType !== 1) return 0;
  if (el.dataset.rivyoReadMoreTranslated === "1") return 0;

  const original = (el.getAttribute("data-desc") || "").trim();
  if (!original) {
    el.dataset.rivyoReadMoreTranslated = "1";
    return 0;
  }

  // IMPORTANT: Rivyo sometimes already sets data-desc in EN.
  // Only overwrite if we find a hit for the current snippet text OR current data-desc text.
  // We'll try data-desc first (best), then fall back to the snippet near it.
  let hit = lookup(original, idx);

  if (!hit) {
    const snippet = el.closest("p.description")?.querySelector("span:first-child")?.textContent?.trim();
    if (snippet) hit = lookup(snippet, idx);
  }

  const body = hit && typeof hit.body === "string" ? hit.body.trim() : "";
  if (!body) return 0;

    const li = el.closest("li.wc_review_widget_item");
  if (li && hit && typeof hit.title === "string" && hit.title.trim()) {
    li.dataset.rivyoEnTitle = hit.title.trim();
  }

  el.setAttribute("data-desc", body);
  el.dataset.rivyoReadMoreTranslated = "1";
  return 1;
}

/* ------------------- Lookup helpers ------------------- */

function norm(s) {
  return (s || "")
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .normalize("NFKC")
    .trim();
}

function stripEllipsis(s) {
  return (s || "").replace(/\s*(\.\.\.|…)\s*$/, "").trim();
}

function endsWithEllipsis(s) {
  return /\.\.\.\s*$|…\s*$/.test((s || "").trim());
}

function shortenToLength(text, maxLen, addEllipsis) {
  const t = (text || "").trim();
  if (!maxLen || maxLen < 10) return addEllipsis ? stripEllipsis(t) + "..." : t;

  if (t.length <= maxLen) return t;

  // Try to cut on a word boundary near maxLen
  let cut = t.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(" ");
  if (lastSpace > Math.floor(maxLen * 0.65)) cut = cut.slice(0, lastSpace);

  cut = cut.trim();

  return addEllipsis ? cut + "..." : cut;
}

function buildIndexes(rawMap) {
  const exact = new Map();
  const prefix = new Map();

  for (const [k, v] of Object.entries(rawMap || {})) {
    const nk = norm(k);
    if (!nk) continue;

    // Ensure v is an object with possible {title, body}
    if (!v || typeof v !== "object") continue;

    exact.set(nk, v);

    const p = nk.slice(0, 40);
    if (!prefix.has(p)) prefix.set(p, []);
    prefix.get(p).push([nk, v]);
  }
  return { exact, prefix };
}

function lookup(text, idx) {
  const t = norm(text);
  if (!t) return null;

  // Exact
  const hit = idx.exact.get(t);
  if (hit) return hit;

  // Truncated match: if current text ends with "..." or "…"
  const base = norm(stripEllipsis(t));
  if (base && base.length >= 12) {
    const p = base.slice(0, 40);
    const bucket = idx.prefix.get(p) || [];
    for (const [fullKey, val] of bucket) {
      if (fullKey.startsWith(base)) return val;
    }
  }

  return null;
}

function translateRivyoUiText(root) {
  const scope = root || document.querySelector("#wc_review_section") || document.body;
  if (!scope) return 0;

  let count = 0;

  for (const sel of UI_TEXT_SELECTORS) {
    const nodes = scope.querySelectorAll(sel);
    for (const el of nodes) {
      if (!el || el.nodeType !== 1) continue;

      // avoid hammering forever
      if (el.dataset.rivyoUiTranslated === "1") continue;

      const txt = (el.textContent || "").trim();
      if (!txt) { el.dataset.rivyoUiTranslated = "1"; continue; }

      // Exact replacements first
      if (RIVYO_UI_TRANSLATIONS[txt]) {
        el.textContent = RIVYO_UI_TRANSLATIONS[txt];
        el.dataset.rivyoUiTranslated = "1";
        count++;
        continue;
      }

      // Special case: "Baseado em 18 avaliações" => "Based on 18 reviews"
      if (/^Baseado em\s+\d+\s+avaliaç(ão|ões)$/i.test(txt)) {
        el.textContent = txt
          .replace(/^Baseado em/i, "Based on")
          .replace(/avaliaç(ão|ões)$/i, "reviews");
        el.dataset.rivyoUiTranslated = "1";
        count++;
        continue;
      }

      // Special case: "Avaliações (18)" => "Reviews (18)"
      if (/^Avaliações\s*\(\d+\)$/i.test(txt)) {
        el.textContent = txt.replace(/^Avaliações/i, "Reviews");
        el.dataset.rivyoUiTranslated = "1";
        count++;
        continue;
      }

      // Special case: Rivyo sometimes renders "Eu recomendo este produto" with extra whitespace
      if (txt.includes("Eu recomendo este produto")) {
        el.textContent = txt.replace("Eu recomendo este produto", "I recommend this product");
        el.dataset.rivyoUiTranslated = "1";
        count++;
        continue;
      }

      // If no match, don't mark translated; Rivyo may re-render later with different text
    }
  }

  return count;
}

/* ------------------- Observer (debounced) ------------------- */

function observeRivyo(root, onChange) {
  const run = debounce(() => {
    try { onChange(); } catch (e) { console.error("[Rivyo translate] observer error", e); }
  }, 180);

  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === "childList" && (m.addedNodes?.length || m.removedNodes?.length)) {
        run();
        return;
      }
    }
  });

  obs.observe(root, { childList: true, subtree: true });
  log("[Rivyo translate] observer attached");
}

function wireUserActions(reRun) {
  const root = document.querySelector("#wc_review_section") || document.body;
  if (!root) return;

  const handler = debounce(() => reRun(), 150);

  root.addEventListener("click", (e) => {
    const t = e.target;
    if (!t) return;
    if (
      t.closest("#review_pagination") ||
      t.closest(".wc_pagination") ||
      t.closest(".wc_rivyo_review_filter") ||
      t.closest(".wc_rating_filter")
    ) {
      handler();
    }
  }, true);

  root.addEventListener("change", (e) => {
    const t = e.target;
    if (t && (t.matches("#sort_by") || t.closest(".wc_rivyo_review_filter"))) {
      handler();
    }
  }, true);

  log("[Rivyo translate] user action hooks attached");
}

/* ------------------- Shopify asset url ------------------- */

function getShopifyAssetUrl(filename) {
  const current = document.currentScript;
  const src = current && current.src ? current.src : findScriptSrcByFilename("rivyo-translate.js");
  if (!src) return null;

  const url = new URL(src, location.origin);
  url.pathname = url.pathname.replace(/\/assets\/[^/]+$/, `/assets/${filename}`);
  if (!url.search) url.search = "?v=" + Date.now();
  return url.toString();
}

function findScriptSrcByFilename(file) {
  for (const s of document.scripts) {
    if (s.src && s.src.includes(`/assets/${file}`)) return s.src;
  }
  return null;
}

async function fetchJsonNoCache(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to fetch map: ${res.status} ${res.statusText}`);
  return await res.json();
}

/* ------------------- Utils ------------------- */

function waitFor(predicate, timeoutMs = 8000) {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const tick = () => {
      try {
        const val = predicate();
        if (val) return resolve(val);
      } catch {}
      if (Date.now() - start > timeoutMs) return reject(new Error("timeout"));
      requestAnimationFrame(tick);
    };
    tick();
  });
}

function debounce(fn, ms) {
  let t = null;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function log(...args) {
  if (!DEBUG) return;
  console.log(...args);
}